import { IonPage } from '@ionic/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Screens, useAnalytics } from '../AppAnalyticsProvider';
import { useHistory } from 'react-router';
import CustomHelmet from '../components/ComponentsFunctional/CustomHelmet/CustomHelmet';

const APP_STORE_URL = 'https://apps.apple.com/us/app/ecliptic-ski-snowboard-app/id1607046376';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=xyz.infinitedegrees.ride';
const CDN_MOCKUPS = `${process.env.REACT_APP_CDN_BASE}/mockups`;

const AppleIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" className="w-[26px] h-[26px] shrink-0 text-electric-blue" aria-hidden="true">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" fill="currentColor"/>
    </svg>
);

const AndroidIcon: React.FC = () => (
    <svg viewBox="0 0 24 24" className="w-[26px] h-[26px] shrink-0 text-[#3DDC84]" aria-hidden="true">
        <path d="M17.523 15.341c-.413 0-.75-.336-.75-.75V8.75c0-.413.337-.75.75-.75s.75.337.75.75v5.841c0 .414-.337.75-.75.75zm-11.046 0c-.413 0-.75-.336-.75-.75V8.75c0-.413.337-.75.75-.75s.75.337.75.75v5.841c0 .414-.337.75-.75.75zM8.5 18.5v2.25a1.25 1.25 0 0 0 2.5 0V18.5h2a1.25 1.25 0 0 0 2.5 0V18.5h.023a2 2 0 0 0 2-2V8H5.977v8.5a2 2 0 0 0 2 2H8.5zm3.5-13.868l.918-1.655a.3.3 0 1 0-.524-.291L11.46 4.4a6.108 6.108 0 0 0-1.965.003L8.573 2.68a.3.3 0 1 0-.523.292L9 4.628A6.026 6.026 0 0 0 6 9.977h12A6.026 6.026 0 0 0 15 4.628z" fill="currentColor"/>
    </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg viewBox="0 0 20 20" className={className || "w-[13px] h-[13px] text-yellow-400 shrink-0"} aria-hidden="true">
        <path d="M10 1l2.39 4.84 5.34.78-3.87 3.77.91 5.33L10 13.27l-4.77 2.51.91-5.33-3.87-3.77 5.34-.78L10 1z" fill="currentColor"/>
    </svg>
);

const LaurelLeft: React.FC = () => (
    <svg viewBox="0 0 24 48" className="h-10 w-5 text-white shrink-0 translate-x-[3px]" aria-hidden="true">
        <g fill="currentColor" fillRule="evenodd">
            <path d="M18 46 C16 38, 10 28, 8 20 C6 12, 8 4, 12 1" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.4"/>
            <path d="M17 42 C12 40, 8 36, 6 32 C10 32, 14 34, 17 42Z" opacity="0.55"/>
            <path d="M15 36 C10 34, 6 30, 4 26 C8 26, 12 28, 15 36Z" opacity="0.65"/>
            <path d="M13 30 C8 28, 4 24, 2 20 C6 20, 10 22, 13 30Z" opacity="0.75"/>
            <path d="M11 24 C6 22, 3 18, 2 14 C6 14, 9 16, 11 24Z" opacity="0.85"/>
            <path d="M10 18 C6 16, 4 12, 4 8 C7 9, 9 12, 10 18Z" opacity="0.9"/>
            <path d="M11 12 C8 10, 7 6, 8 3 C10 4, 11 8, 11 12Z"/>
        </g>
    </svg>
);

const LaurelRight: React.FC = () => (
    <svg viewBox="0 0 24 48" className="h-10 w-5 text-white shrink-0 -translate-x-[3px]" aria-hidden="true">
        <g fill="currentColor" fillRule="evenodd">
            <path d="M6 46 C8 38, 14 28, 16 20 C18 12, 16 4, 12 1" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.4"/>
            <path d="M7 42 C12 40, 16 36, 18 32 C14 32, 10 34, 7 42Z" opacity="0.55"/>
            <path d="M9 36 C14 34, 18 30, 20 26 C16 26, 12 28, 9 36Z" opacity="0.65"/>
            <path d="M11 30 C16 28, 20 24, 22 20 C18 20, 14 22, 11 30Z" opacity="0.75"/>
            <path d="M13 24 C18 22, 21 18, 22 14 C18 14, 15 16, 13 24Z" opacity="0.85"/>
            <path d="M14 18 C18 16, 20 12, 20 8 C17 9, 15 12, 14 18Z" opacity="0.9"/>
            <path d="M13 12 C16 10, 17 6, 16 3 C14 4, 13 8, 13 12Z"/>
        </g>
    </svg>
);

const StoreButton: React.FC<{ platform: 'ios' | 'android'; large?: boolean }> = ({ platform, large }) => {
    const isIOS = platform === 'ios';
    return (
        <a
            href={isIOS ? APP_STORE_URL : PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-[11px] no-underline whitespace-nowrap text-white
                transition-transform duration-200 motion-reduce:transition-none
                hover:-translate-y-0.5 motion-reduce:hover:transform-none
                ${large ? 'py-4 px-7 rounded-2xl' : 'py-[13px] px-[22px] rounded-[14px]'}
                sm:w-auto ${large ? 'w-full max-w-[280px] justify-center' : 'w-full sm:w-auto justify-center sm:justify-start'}`}
            style={isIOS ? {
                background: 'rgba(10, 255, 255, 0.06)',
                border: '1px solid rgba(10, 255, 255, 0.32)',
                boxShadow: '0 0 22px rgba(10, 255, 255, 0.08)',
            } : {
                background: 'rgba(61, 220, 132, 0.06)',
                border: '1px solid rgba(61, 220, 132, 0.32)',
                boxShadow: '0 0 22px rgba(61, 220, 132, 0.08)',
            }}
            aria-label={isIOS ? 'Download Ecliptic on the App Store' : 'Get Ecliptic on Google Play'}
        >
            {isIOS ? <AppleIcon /> : <AndroidIcon />}
            <div className="flex flex-col gap-px">
                <span className="text-[10px] font-bold text-white/50 tracking-[0.6px] uppercase leading-none">
                    {isIOS ? 'Download on the' : 'Get it on'}
                </span>
                <span className="text-[17px] font-bold text-white tracking-[-0.3px] leading-[1.15]">
                    {isIOS ? 'App Store' : 'Google Play'}
                </span>
            </div>
        </a>
    );
};

const WebLandingPage: React.FC = () => {
    const history = useHistory();
    const [menuOpen, setMenuOpen] = useState(false);
    const { trackScreen } = useAnalytics();

    useEffect(() => {
        trackScreen(Screens.LANDING);
    }, [trackScreen]);

    const { showIOS, showAndroid } = useMemo(() => {
        const ua = navigator.userAgent || navigator.vendor || (window as any).opera || '';
        const ios = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
        const android = /android/i.test(ua);
        const desktop = !ios && !android;
        return {
            showIOS: ios || desktop,
            showAndroid: android || desktop,
        };
    }, []);

    const goToLogin = () => {
        setMenuOpen(false);
        history.push('/flow/login');
    };

    const scrollToDownload = () => {
        document.getElementById('download-section')?.scrollIntoView({ behavior: 'smooth' });
        setMenuOpen(false);
    };

    return (
        <IonPage>
            <CustomHelmet
                title="Ecliptic — Your Riding Deserves More Than a Hashtag"
                description="The app for park skiers and snowboarders serious about progression. Film tricks, build your bag, analyze your riding, and connect with the community. Ski & Snowboard App of the Year. Free on iOS and Android."
                url="https://ecliptic.day"
            />
            <div className="absolute inset-0 overflow-y-auto overflow-x-hidden bg-black text-white font-['Mukta_Mahee'] scroll-smooth">

                {/* ── NAV ─────────────────────────────────────────── */}
                <nav className="sticky top-0 z-[100] bg-[rgba(0,0,0,0.75)] backdrop-blur-[24px] border-b border-[rgba(88,97,172,0.18)]">
                    <div className="max-w-[1200px] mx-auto px-7 h-16 flex items-center justify-between">
                        <div className="flex items-center">
                            <img
                                src="/assets/logo/ECLIPTIC_NEW_WHITE_PNG_NO_PADDING.png"
                                alt="Ecliptic"
                                className="h-[30px] w-auto object-contain block"
                            />
                        </div>

                        <div className="hidden md:flex items-center gap-7">
                            <a href="/shop" target="_blank" rel="noopener noreferrer" className="text-white/65 no-underline text-sm font-bold tracking-[0.4px] transition-colors duration-200 hover:text-white">
                                Shop
                            </a>
                            <a href="/blog" className="text-white/65 no-underline text-sm font-bold tracking-[0.4px] transition-colors duration-200 hover:text-white">
                                Blog
                            </a>
                            <button
                                type="button"
                                className="bg-transparent border-none p-0 cursor-pointer font-['Mukta_Mahee'] text-white/65 no-underline text-sm font-bold tracking-[0.4px] transition-colors duration-200 hover:text-white"
                                onClick={goToLogin}
                            >
                                Sign In
                            </button>
                            <button
                                type="button"
                                className="bg-gradient-to-br from-highlight to-highlight-alt text-white border-none py-[9px] px-[22px] rounded-[22px] text-sm font-bold cursor-pointer tracking-[0.3px] transition-all duration-[180ms] font-['Mukta_Mahee'] whitespace-nowrap hover:opacity-[0.88] hover:-translate-y-px motion-reduce:transition-none motion-reduce:hover:transform-none"
                                onClick={scrollToDownload}
                            >
                                Get the App
                            </button>
                        </div>

                        <button
                            type="button"
                            className="flex md:hidden flex-col gap-[5px] bg-transparent border-none cursor-pointer py-[6px] px-1"
                            onClick={() => setMenuOpen(v => !v)}
                            aria-label="Menu"
                        >
                            <span className={`block w-[22px] h-0.5 bg-white/85 rounded-sm transition-all duration-200 ${menuOpen ? 'translate-y-[7px] rotate-45' : ''}`}></span>
                            <span className={`block w-[22px] h-0.5 bg-white/85 rounded-sm transition-all duration-200 ${menuOpen ? 'opacity-0' : ''}`}></span>
                            <span className={`block w-[22px] h-0.5 bg-white/85 rounded-sm transition-all duration-200 ${menuOpen ? '-translate-y-[7px] -rotate-45' : ''}`}></span>
                        </button>
                    </div>

                    {menuOpen && (
                        <div className="bg-[rgba(5,5,15,0.98)] border-t border-[rgba(88,97,172,0.15)] px-7 pt-3 pb-5 flex flex-col gap-1">
                            <button
                                type="button"
                                className="block w-full text-left bg-transparent border-none p-0 cursor-pointer font-['Mukta_Mahee'] text-white/75 no-underline text-base font-bold py-3 border-b border-white/5 transition-colors duration-[180ms] hover:text-white"
                                onClick={goToLogin}
                            >
                                Sign In
                            </button>
                            {showIOS && (
                                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
                                    className="block w-full text-left text-white/75 no-underline text-base font-bold py-3 border-b border-white/5 transition-colors duration-[180ms] hover:text-white">
                                    Download for iPhone
                                </a>
                            )}
                            {showAndroid && (
                                <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer"
                                    className="block w-full text-left text-white/75 no-underline text-base font-bold py-3 border-b border-white/5 transition-colors duration-[180ms] hover:text-white">
                                    Download for Android
                                </a>
                            )}
                            <a href="/shop" target="_blank" rel="noopener noreferrer"
                                className="block w-full text-left text-white/75 no-underline text-base font-bold py-3 border-b border-white/5 transition-colors duration-[180ms] hover:text-white">
                                Shop
                            </a>
                            <a href="/blog"
                                className="block w-full text-left text-white/75 no-underline text-base font-bold py-3 transition-colors duration-[180ms] hover:text-white">
                                Blog
                            </a>
                        </div>
                    )}
                </nav>

                {/* ── HERO ─────────────────────────────────────────── */}
                <section
                    className="relative min-h-[70vh] lg:min-h-[calc(100vh-64px)] flex flex-col overflow-hidden"
                    style={{ background: 'linear-gradient(160deg, #08080f 0%, #0d0d1e 35%, #0a0a16 65%, #050508 100%)' }}
                >
                    {/* Grid texture */}
                    <div className="absolute inset-0 pointer-events-none" style={{
                        background: 'linear-gradient(to right, rgba(88,97,172,0.055) 1px, transparent 1px), linear-gradient(to bottom, rgba(88,97,172,0.055) 1px, transparent 1px)',
                        backgroundSize: '64px 64px',
                    }} />

                    {/* Top radial glow */}
                    <div
                        className="absolute left-1/2 -translate-x-1/2 w-[900px] h-[500px] pointer-events-none z-0"
                        style={{
                            top: '-15%',
                            background: 'radial-gradient(ellipse at center, rgba(88,97,172,0.18) 0%, transparent 68%)',
                        }}
                    />

                    <div className="relative z-[2] flex-1 max-w-[1200px] mx-auto w-full
                        px-5 pt-12 pb-[60px]
                        md:px-7 md:pt-[60px] md:pb-[80px]
                        lg:pt-[88px] lg:pb-[180px]
                        flex items-center gap-16
                        text-center md:text-center lg:text-left">
                        <div className="flex-1 min-w-0 flex flex-col items-center lg:items-start">
                            <div className="inline-flex items-center gap-[9px] bg-[rgba(88,97,172,0.12)] border border-[rgba(88,97,172,0.38)] rounded-3xl py-[7px] px-4 text-[13px] font-bold text-white/[0.72] mb-8 tracking-[0.2px] self-center lg:self-start">
                                <span className="inline-flex items-center gap-px">
                                    <StarIcon /><StarIcon /><StarIcon /><StarIcon /><StarIcon />
                                </span>
                                4.9 Stars &middot; Push Your Park Progression
                            </div>

                            <h1
                                className="text-[clamp(52px,8vw,96px)] font-bold leading-[1.02] tracking-[-2px] mb-7 bg-clip-text"
                                style={{
                                    background: 'linear-gradient(140deg, #ffffff 0%, rgba(255,255,255,0.9) 45%, #9ba3d4 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}
                            >
                                Your riding<br />deserves more<br />than a hashtag.
                            </h1>

                            <p className="text-[clamp(15px,1.8vw,18px)] leading-[1.65] text-white/[0.88] max-w-[440px] md:max-w-full lg:max-w-[440px] mb-11 md:text-center lg:text-left">
                                The app for park skiers and snowboarders who are serious
                                about progression. Film your tricks, build your bag, analyze
                                your riding, and connect with the community that actually gets it.
                            </p>

                            <div className="flex flex-wrap gap-[14px] flex-col sm:flex-row items-center sm:items-start w-full max-w-[280px] sm:max-w-none sm:w-auto justify-center lg:justify-start self-center md:self-center lg:self-start">
                                {showIOS && <StoreButton platform="ios" />}
                                {showAndroid && <StoreButton platform="android" />}
                            </div>

                            <div className="flex lg:hidden justify-center mt-10 relative">
                                <picture>
                                    <source srcSet={`${CDN_MOCKUPS}/hero-community.webp`} type="image/webp" />
                                    <img
                                        src={`${CDN_MOCKUPS}/hero-community.png`}
                                        alt="Ecliptic app showing a snowboard trick clip with trick tags, scores, and community interaction"
                                        className="w-[240px] md:w-[280px] h-auto rounded-xl"
                                        style={{ filter: 'drop-shadow(0 24px 60px rgba(0,0,0,0.6)) drop-shadow(0 0 40px rgba(88,97,172,0.15))' }}
                                        width={390}
                                        height={844}
                                    />
                                </picture>
                                <div
                                    className="absolute -inset-[30px] pointer-events-none -z-[1]"
                                    style={{ background: 'radial-gradient(ellipse at center, rgba(88,97,172,0.2) 0%, transparent 70%)' }}
                                />
                            </div>
                        </div>

                        <div className="hidden lg:flex shrink-0 relative justify-center items-center">
                            <picture>
                                <source srcSet={`${CDN_MOCKUPS}/hero-community.webp`} type="image/webp" />
                                <img
                                    src={`${CDN_MOCKUPS}/hero-community.png`}
                                    alt="Ecliptic app showing a snowboard trick clip with trick tags, scores, and community interaction"
                                    className="w-[320px] h-auto rounded-xl"
                                    style={{ filter: 'drop-shadow(0 24px 60px rgba(0,0,0,0.6)) drop-shadow(0 0 40px rgba(88,97,172,0.15))' }}
                                    width={390}
                                    height={844}
                                />
                            </picture>
                            <div
                                className="absolute -inset-[30px] pointer-events-none -z-[1]"
                                style={{ background: 'radial-gradient(ellipse at center, rgba(88,97,172,0.2) 0%, transparent 70%)' }}
                            />
                        </div>
                    </div>

                    {/* Mountain silhouette */}
                    <div className="absolute bottom-0 left-0 right-0 z-[1] leading-[0] pointer-events-none" aria-hidden="true">
                        <svg viewBox="0 0 1440 220" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block">
                            <path d="M0,220 L0,160 L90,110 L160,140 L240,80 L310,120 L390,55 L460,95 L540,35 L610,75 L680,20 L750,60 L830,30 L900,65 L970,42 L1040,72 L1110,50 L1180,78 L1250,58 L1330,85 L1440,65 L1440,220 Z" fill="#0a0a14"/>
                            <path d="M0,220 L0,175 L80,155 L150,165 L220,140 L290,155 L360,130 L430,148 L500,125 L570,142 L640,120 L710,138 L780,118 L850,135 L920,115 L990,132 L1060,118 L1130,135 L1200,122 L1280,138 L1370,125 L1440,135 L1440,220 Z" fill="#080810" opacity="0.85"/>
                        </svg>
                    </div>
                </section>

                {/* ── SOCIAL PROOF ────────────────────────────────── */}
                <section className="bg-[#050508] border-y border-[rgba(88,97,172,0.1)] py-7 px-7">
                    <div className="max-w-[1100px] mx-auto flex items-center justify-center gap-8 flex-wrap
                        md:flex-row flex-col md:gap-8 gap-4 md:text-left text-center">
                        <div className="flex items-center gap-2 justify-center">
                            <span className="inline-flex items-center gap-0.5">
                                <StarIcon className="w-[15px] h-[15px] text-yellow-400 shrink-0" />
                                <StarIcon className="w-[15px] h-[15px] text-yellow-400 shrink-0" />
                                <StarIcon className="w-[15px] h-[15px] text-yellow-400 shrink-0" />
                                <StarIcon className="w-[15px] h-[15px] text-yellow-400 shrink-0" />
                                <StarIcon className="w-[15px] h-[15px] text-yellow-400 shrink-0" />
                            </span>
                            <span className="text-sm font-bold text-white/55 tracking-[0.2px]">4.9 stars on iOS &amp; Android</span>
                        </div>

                        <div className="w-px h-6 md:block hidden bg-white/10 shrink-0" />
                        <div className="w-10 h-px md:hidden block bg-white/10 shrink-0" />

                        <div className="flex items-center gap-1 justify-center">
                            <LaurelLeft />
                            <span className="text-[11px] font-bold text-white italic tracking-[0.5px] text-center leading-[1.3] uppercase">
                                Ski &amp; Snowboard<br />App of the Year
                            </span>
                            <LaurelRight />
                        </div>

                        <div className="w-px h-6 md:block hidden bg-white/10 shrink-0" />
                        <div className="w-10 h-px md:hidden block bg-white/10 shrink-0" />

                        <div className="flex items-baseline gap-2 md:flex-row flex-col md:items-baseline items-center md:gap-2 gap-1 text-center md:text-left">
                            <span className="text-[15px] font-bold text-white/75 italic">&ldquo;Cool&rdquo;</span>
                            <span className="text-xs font-bold text-white/35 tracking-[0.2px]">&mdash; Liam Brearley, Pro Snowboarder</span>
                        </div>
                    </div>
                </section>

                {/* ── HOW IT WORKS — 4 features with real screenshots ── */}
                <section className="bg-[#050508] py-20 md:py-[104px] px-6 md:px-7">
                    <h2 className="text-center text-[clamp(26px,3.5vw,40px)] font-bold mb-[72px] text-white/90 tracking-[-0.5px]">
                        How it works
                    </h2>

                    {[
                        { img: 'feed-control', alt: 'Ecliptic feed with TV, Following, New, and Hot tabs showing snowboard clips', title: 'Control Your Feed', desc: <>Four feeds. Your rules. Browse the global feed on Explore, see what your crew is posting on Following, catch the freshest uploads on New, or find what&rsquo;s blowing up on Hot. Every clip tagged with sport, feature, trick name, and location so you find exactly what you&rsquo;re looking for.</> },
                        { img: 'trick-tagging', alt: 'Ecliptic trick upload screen with direction, axis, degree, grab, and flavor tags', title: 'Tag Every Trick', desc: 'Not just a caption. Tag your backside rodeo 5 melon, your front 3 safety, your pretzel 270 out. Direction, axis, rotation, grab, style — the full breakdown. Every trick you land goes in your bag, searchable forever.', reverse: true },
                        { img: 'trick-search', alt: 'Ecliptic search showing trick filter with clips, riders, and map tabs', title: 'Search & Filter Everything', desc: "Want to find every backflip ever landed? Every rider sending double corks at Breck? Every frontboard clip from last season? Filter by trick, rider, feature, spot, or sport. Clips, Riders, Map — three views, one search." },
                        { img: 'events', alt: 'Ecliptic event page showing Turf Wars with leaderboard rankings and sport tags', title: 'Enter Events. Win Prizes.', desc: "Drop into community events and brand challenges like Turf Wars. Upload your best clips across jumps, rails, jibs, the halfpipe, freeride terrain, and or any and all off-snow training grounds. Climb the leaderboard, earn Bisk, win gear and pins from sponsors. Open to skiers and snowboarders — your whole crew can enter.", reverse: true },
                    ].map((feature, i) => (
                        <div key={i} className={`max-w-[1000px] mx-auto ${i < 3 ? 'mb-16 md:mb-20' : ''} flex items-center gap-8 lg:gap-14
                            flex-col text-center lg:text-left ${feature.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'}`}>
                            <div className="flex-none lg:flex-[0_0_300px] flex justify-center">
                                <picture>
                                    <source srcSet={`${CDN_MOCKUPS}/${feature.img}.webp`} type="image/webp" />
                                    <img
                                        src={`${CDN_MOCKUPS}/${feature.img}.png`}
                                        alt={feature.alt}
                                        className="w-[200px] sm:w-[240px] lg:w-[280px] h-auto rounded-[10px]"
                                        style={{ filter: 'drop-shadow(0 16px 48px rgba(0,0,0,0.5)) drop-shadow(0 0 24px rgba(88,97,172,0.1))' }}
                                        width={390}
                                        height={844}
                                        loading="lazy"
                                    />
                                </picture>
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-[clamp(22px,3vw,30px)] font-bold mb-[14px] text-white/90 tracking-[-0.3px]">
                                    {feature.title}
                                </h3>
                                <p className="text-base leading-[1.7] text-white/[0.88] m-0 lg:max-w-[440px]">
                                    {feature.desc}
                                </p>
                            </div>
                        </div>
                    ))}
                </section>

                {/* ── WHAT MAKES IT DIFFERENT — with mockup screenshots ── */}
                <section
                    className="py-20 md:py-[104px] px-6 md:px-7 border-t border-[rgba(88,97,172,0.08)]"
                    style={{ background: 'linear-gradient(175deg, #08080f 0%, #050508 100%)' }}
                >
                    <div className="max-w-[1100px] mx-auto">
                        <h2 className="text-center text-[clamp(26px,3.5vw,40px)] font-bold mb-[72px] text-white/90 tracking-[-0.5px]">
                            Built for the terrain park. Not the timeline.
                        </h2>
                        <p className="text-center text-[clamp(15px,1.6vw,17px)] leading-[1.65] text-white/[0.88] max-w-[540px] mx-auto -mt-10 mb-14">
                            Instagram buries your clips in an algorithm. NewSchoolers is stuck
                            in 2010. Strava counts miles, not corks. Ecliptic is the only platform
                            built for park skiing and snowboarding progression.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
                            {[
                                { img: 'stats', alt: 'Lifty AI stats showing radar chart of skiing skill levels', title: 'Track Progression', desc: 'Lifty AI analyzes your clips and gives you stats on your riding. See your levels, progression over time, and what to work on next.' },
                                { img: 'map', alt: '3D Mapbox map showing terrain parks and trick locations worldwide', title: '3D Park Maps', desc: 'Explore terrain parks worldwide in 3D. Find new spots, discover what riders are hitting near you, and pin your favorite features.' },
                                { img: 'bisk', alt: 'Bisk currency screen showing send interface with balance', title: 'Earn & Spend Bisk', desc: 'Earn Bisk from sponsors and riders for putting down hammers. Send it to riders who inspire you. Swap it for gear, event entries, and exclusive content.' },
                                { img: 'crews', alt: 'Crew page showing events, followers, and riders', title: 'Crews & Community', desc: 'Build your crew. Rep your mountain. Connect with organizations, academies, shops, and brands. Discover crews worldwide.' },
                            ].map((item, i) => (
                                <div key={i} className="p-5 pb-7 rounded-[18px] bg-white/[0.02] border border-white/5 flex flex-col
                                    transition-all duration-[220ms] hover:-translate-y-[3px] hover:border-[rgba(88,97,172,0.22)]
                                    motion-reduce:transition-none motion-reduce:hover:transform-none">
                                    <picture>
                                        <source srcSet={`${CDN_MOCKUPS}/${item.img}.webp`} type="image/webp" />
                                        <img
                                            src={`${CDN_MOCKUPS}/${item.img}.png`}
                                            alt={item.alt}
                                            className="w-full h-auto rounded-[10px] mb-[18px]"
                                            style={{ filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.4))' }}
                                            width={390}
                                            height={844}
                                            loading="lazy"
                                        />
                                    </picture>
                                    <h3 className="text-[17px] font-bold text-white/90 mb-2 tracking-[-0.2px]">{item.title}</h3>
                                    <p className="text-sm leading-[1.6] text-white/[0.88] m-0">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── DOWNLOAD CTA ─────────────────────────────────── */}
                <section
                    className="py-20 md:py-[104px] px-6 md:px-7 text-center relative overflow-hidden"
                    style={{ background: 'linear-gradient(150deg, #0a0a14 0%, #080810 100%)' }}
                    id="download-section"
                >
                    <div
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] pointer-events-none"
                        style={{ background: 'radial-gradient(ellipse at center, rgba(88,97,172,0.14) 0%, transparent 70%)' }}
                    />
                    <div className="relative z-[1] max-w-[580px] mx-auto">
                        <h2
                            className="text-[clamp(30px,4.5vw,52px)] font-bold tracking-[-1px] mb-4 bg-clip-text"
                            style={{
                                background: 'linear-gradient(135deg, #ffffff 0%, #9ba3d4 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                            }}
                        >
                            Start building your bag.
                        </h2>
                        <p className="text-[17px] text-white/[0.88] leading-[1.65] mb-11">
                            Free on iOS and Android. Join the riders who are done
                            posting clips into the void.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4 mb-7 flex-col sm:flex-row items-center">
                            {showIOS && <StoreButton platform="ios" large />}
                            {showAndroid && <StoreButton platform="android" large />}
                        </div>
                        <p className="text-sm text-white/[0.38] m-0">
                            Already have an account?{' '}
                            <button
                                type="button"
                                className="bg-transparent border-none p-0 cursor-pointer font-['Mukta_Mahee'] text-[rgba(88,97,172,0.9)] no-underline font-bold transition-colors duration-[180ms] hover:text-[#a0a8d8]"
                                onClick={goToLogin}
                            >
                                Sign in
                            </button>
                        </p>
                    </div>
                </section>

                {/* ── FOOTER ───────────────────────────────────────── */}
                <footer className="bg-black border-t border-white/[0.055] pt-16 pb-9 px-7">
                    <div className="max-w-[1100px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr] gap-7 sm:gap-9 lg:gap-12">
                        <div className="flex flex-col items-center text-center gap-[14px] sm:col-span-2 lg:col-span-1">
                            <img
                                src="/assets/logo/ECLIPTIC_NEW_WHITE_PNG_NO_PADDING.png"
                                alt="Ecliptic"
                                className="h-[26px] w-auto object-contain block opacity-90"
                            />
                            <p className="text-[13px] leading-[1.55] text-white/65 m-0 max-w-[260px] tracking-[0.2px]">
                                The app for park skiers and snowboarders serious about progression.
                            </p>
                        </div>

                        <div className="flex flex-col gap-[10px]">
                            <h4 className="text-[13px] font-bold text-white/95 uppercase tracking-[0.8px] m-0 mb-1">Company</h4>
                            <a href="/shop" target="_blank" rel="noopener noreferrer" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">Shop</a>
                            <a href="/blog" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">Blog</a>
                            <a href="/invest" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">Invest / Partner</a>
                            <a href="/careers" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">Careers</a>
                            <a href="sms:+18556292475" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">Text Us!</a>
                            <a href="mailto:mia@idx.style" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">Contact</a>
                            <a href="/privacy" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">Privacy Policy</a>
                            <a href="/terms" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">Terms of Service</a>
                            <a href="/eula" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">EULA</a>
                        </div>

                        <div className="flex flex-col gap-[10px]">
                            <h4 className="text-[13px] font-bold text-white/95 uppercase tracking-[0.8px] m-0 mb-1">Downloads</h4>
                            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">iPhone</a>
                            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">Android</a>
                            <a href="https://ecliptic.day" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">Web</a>
                        </div>

                        <div className="flex flex-col gap-[10px]">
                            <h4 className="text-[13px] font-bold text-white/95 uppercase tracking-[0.8px] m-0 mb-1">Socials</h4>
                            <a href="https://tiktok.com/@ecliptic.day" target="_blank" rel="noopener noreferrer" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">TikTok</a>
                            <a href="https://linkedin.com/company/ecliptic-day" target="_blank" rel="noopener noreferrer" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">LinkedIn</a>
                            <a href="https://twitter.com/eclipticday" target="_blank" rel="noopener noreferrer" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">Twitter / X</a>
                            <a href="https://instagram.com/ecliptic.day" target="_blank" rel="noopener noreferrer" className="text-white/[0.72] no-underline text-[13px] font-bold tracking-[0.2px] transition-colors duration-[180ms] hover:text-white">Instagram</a>
                        </div>
                    </div>

                    <div className="max-w-[1100px] mx-auto mt-12 pt-6 border-t border-white/5 text-center">
                        <p className="text-xs text-white/45 m-0">&copy; 2026 Infinite Degrees LLC. All rights reserved.</p>
                    </div>
                </footer>

            </div>
        </IonPage>
    );
};

export default WebLandingPage;
